import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme as withStyledTheme } from 'styled-components';
import Select from 'react-dropdown-select';
import { withTheme } from '../../theme';
import { withProps } from 'recompose';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';

// Utility to move an item in an array
function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArray = array.slice();
  const [movedItem] = newArray.splice(from, 1);
  newArray.splice(to, 0, movedItem);
  return newArray;
}

const StyledItem = styled.div<{ disabled?: boolean }>`
  background: ${withTheme(theme => theme.backgroundColor.string())};
  line-height: ${withTheme(theme => theme.lineHeight.xl)};
  padding-left: ${withTheme(theme => theme.input.padding)};
  cursor: pointer;
  display: flex;
  align-items: center;
  > div {
    display: flex;
    align-items: center;
  }
  input {
    margin-right: 1em;
  }
  :hover {
    background: ${({ theme }) => theme.backgroundColor.darken(0.05).string()};
  }
  opacity: ${({ disabled }) => (disabled ? 0.85 : 1)};
`;

const customStyles = (theme: any) => ({
  padding: 'calc(1rem * 0.75 - 0.125rem)',
  border: `2px solid ${theme.border.color.default.string()}`,
  cursor: 'pointer',
});

const ContentLabel = ({ props, state, label }: { props: any; state: any; label: string }) => (
  <span>
    {label || 'Configure Columns'} {`(${state.values.length} of ${props.options.length} columns)`}
  </span>
);

/**
 * DraggableItem renders each column in the dropdown with a drag handle and a checkbox.
 * The checkbox reflects the column's "visible" flag.
 */
type DraggableItemProps = {
  option: any;
  isDisabled: boolean;
  methods: any;
  valueField: string;
  labelField: string;
};

const DraggableItem: React.FC<DraggableItemProps> = memo(({ option, isDisabled, methods, valueField, labelField }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option[valueField],
  });
  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      display: 'flex',
      alignItems: 'center',
    }),
    [transform, transition, isDragging]
  );

  const isSelected = methods.isSelected(option);
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      if (isSelected) {
        methods.removeItem(e, option, false);
      } else {
        methods.addItem(option);
      }
    },
    [isDisabled, isSelected, methods, option]
  );

  return (
    <StyledItem ref={setNodeRef} style={style} disabled={isDisabled}>
      <div {...attributes} {...listeners} style={{ marginRight: '0.5em', cursor: 'grab' }} aria-label="Drag handle">
        <DragHandle />
      </div>
      <input type="checkbox" onChange={handleChange} checked={isSelected} />
      <span>{option[labelField]}</span>
    </StyledItem>
  );
});
DraggableItem.displayName = 'DraggableItem';

/**
 * OptionList renders the dropdown list that allows reordering and toggling column visibility.
 * It operates on the full list of column settings.
 */
type OptionListProps = {
  props: any;
  state: any;
  methods: any;
  buttonRef: React.RefObject<HTMLButtonElement>;
};

const OptionList: React.FC<OptionListProps> = ({ props, state, methods, buttonRef }) => {
  const valueField = props.valueField || 'title';
  const labelField = props.labelField || 'title';
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pendingChangeRef = useRef<any[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const classes = useStyles();

  // Build initial ordered options from storage + props
  const buildOrderedOptions = useCallback(
    (stateValues: any[], propsOptions: any[]) => {
      const result = [...stateValues];
      propsOptions.forEach(option => {
        if (!stateValues.find(v => v[valueField] === option[valueField])) {
          result.push({ ...option, visible: true });
        }
      });
      return result;
    },
    [valueField]
  );

  // We assume state.values holds the full ordered list of column settings.
  const [orderedOptions, setOrderedOptions] = useState<any[]>(() => buildOrderedOptions(state.values, props.options));

  // Update the local ordered options when either props.options or state.values changes
  // But only if we don't have a pending change (to avoid overwriting user's changes)
  useEffect(() => {
    if (!pendingChangeRef.current) {
      setOrderedOptions(buildOrderedOptions(state.values, props.options));
    }
  }, [props.options, state.values, buildOrderedOptions]);

  // Debounced save to parent - prevents UI freeze on rapid clicks
  const debouncedSave = useCallback(
    (newOrdered: any[]) => {
      pendingChangeRef.current = newOrdered;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (pendingChangeRef.current) {
          methods.onChange(pendingChangeRef.current);
          pendingChangeRef.current = null;
        }
      }, 150); // 150ms debounce - fast enough to feel responsive, slow enough to batch rapid clicks
    },
    [methods]
  );

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Save any pending changes before unmount
        if (pendingChangeRef.current) {
          methods.onChange(pendingChangeRef.current);
        }
      }
    },
    [methods]
  );

  /**
   * Handles drag end event to reorder the full list of columns.
   */
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        setOrderedOptions(prev => {
          const oldIndex = prev.findIndex(item => item[valueField] === active.id);
          const newIndex = prev.findIndex(item => item[valueField] === over.id);
          const newOrdered = arrayMove(prev, oldIndex, newIndex);
          debouncedSave(newOrdered);
          return newOrdered;
        });
      }
    },
    [valueField, debouncedSave]
  );

  /**
   * Toggles a column's visibility on.
   */
  const handleAddItem = useCallback(
    (option: any) => {
      setOrderedOptions(prev => {
        const newOrdered = prev.map(item =>
          item[valueField] === option[valueField] ? { ...item, visible: true } : item
        );
        debouncedSave(newOrdered);
        return newOrdered;
      });
    },
    [valueField, debouncedSave]
  );

  /**
   * Toggles a column's visibility off.
   */
  const handleRemoveItem = useCallback(
    (_e: any, option: any) => {
      setOrderedOptions(prev => {
        const newOrdered = prev.map(item =>
          item[valueField] === option[valueField] ? { ...item, visible: false } : item
        );
        debouncedSave(newOrdered);
        return newOrdered;
      });
    },
    [valueField, debouncedSave]
  );

  // Memoize isSelected function
  const isSelected = useCallback(
    (option: any) => {
      const storedValue = orderedOptions.find((v: any) => v[valueField] === option[valueField]);
      return storedValue ? storedValue.visible : true;
    },
    [orderedOptions, valueField]
  );

  // Override default methods with our custom handlers.
  const customMethods = useMemo(
    () => ({
      ...methods,
      addItem: handleAddItem,
      removeItem: handleRemoveItem,
      isSelected,
    }),
    [methods, handleAddItem, handleRemoveItem, isSelected]
  );

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        state.dropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        methods.closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.dropdown, methods.closeDropdown, buttonRef, methods]);

  return (
    <div className={classes.item} ref={dropdownRef}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedOptions.map(item => item[valueField])} strategy={verticalListSortingStrategy}>
          {orderedOptions.map(option => {
            const isSelected = option.visible;
            const isDisabled = option.disabled && isSelected && orderedOptions.filter(x => x.visible).length === 1;
            return (
              <DraggableItem
                key={option[valueField]}
                option={option}
                isDisabled={isDisabled}
                methods={customMethods}
                valueField={valueField}
                labelField={labelField}
              />
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
};

const ColumnSelect = withStyledTheme(
  ({
    theme,
    options,
    values,
    onChange,
    label,
    labelField = 'title',
    valueField = 'title',
    keepOpen,
    onClose,
    buttonRef,
  }: any) => (
    <Select
      multi
      options={options}
      values={values}
      labelField={labelField}
      valueField={valueField}
      style={customStyles(theme)}
      contentRenderer={withProps({ label })(ContentLabel)}
      dropdownRenderer={props => (
        <OptionList
          props={props.props}
          state={props.state}
          methods={{
            ...props.methods,
            onChange,
            closeDropdown: () => {
              onClose();
            },
          }}
          buttonRef={buttonRef}
        />
      )}
      onChange={onChange}
      dropdownHandle={false}
      keepOpen={keepOpen}
    />
  )
);

ColumnSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      visible: PropTypes.bool,
    })
  ).isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      visible: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};

const useStyles = makeStyles(theme => ({
  item: {
    overflow: 'auto',
    minHeight: 100,
    maxHeight: 600,
    backgroundColor: theme.palette.background.default,
  },
}));

export default ColumnSelect;
